<script>
import { zxcvbn } from "@zxcvbn-ts/core";
import { mapActions } from "vuex";

import SnsInput from "@/components/ui/SnsInput.vue";
import { toaster } from "@/plugins/toaster";

export default {
  name: "UserChangePasswordModal",
  components: {
    SnsInput,
  },
  emits: ["close"],
  expose: ["save", "canSave"],
  data() {
    return {
      currentPassword: "",
      newPassword: "",
      newPasswordConfirm: "",
      isLoading: false,
    };
  },
  computed: {
    isAllFieldsFilled() {
      return this.currentPassword.length && this.newPassword.length && this.newPasswordConfirm.length;
    },
    passwordStrength() {
      if (!this.newPassword) return null;

      return zxcvbn(this.newPassword);
    },
    isPasswordStrong() {
      return this.passwordStrength && this.passwordStrength.score >= 3;
    },
    isPasswordsMatching() {
      return this.newPassword === this.newPasswordConfirm;
    },
    isFormValid() {
      return this.isAllFieldsFilled && this.isPasswordStrong && this.isPasswordsMatching;
    },
    canSave() {
      return this.isFormValid && !this.isLoading;
    },
    passwordErrorMessage() {
      if (this.newPassword && this.newPasswordConfirm && !this.isPasswordsMatching) {
        return this.$t("feedback.validation.passwordMismatch");
      }

      if (this.newPassword && this.isPasswordsMatching && !this.isPasswordStrong) {
        return this.$t("feedback.validation.passwordStrength");
      }

      return null;
    },
  },
  methods: {
    ...mapActions(["changePassword"]),
    save() {
      this.updatePassword();
    },
    updatePassword() {
      if (!this.isAllFieldsFilled) {
        toaster.warning(this.$t("feedback.warning.label"), {
          description: this.$t("feedback.validation.allFieldsRequired"),
        });

        return;
      }

      if (!this.isPasswordsMatching) {
        toaster.warning(this.$t("feedback.warning.label"), {
          description: this.$t("feedback.validation.passwordMismatch"),
        });

        return;
      }

      if (!this.isPasswordStrong) {
        toaster.warning(this.$t("feedback.warning.label"), {
          description: this.$t("feedback.validation.passwordStrength"),
        });

        return;
      }

      this.isLoading = true;

      this.changePassword({
        passwordOld: this.currentPassword,
        passwordNew: this.newPassword,
        passwordConfirm: this.newPasswordConfirm,
      })
        .then(() => {
          toaster.success(this.$t("feedback.success.label"), {
            description: this.$t("feedback.success.passwordChanged"),
          });
          this.currentPassword = "";
          this.newPassword = "";
          this.newPasswordConfirm = "";
          this.$emit("close");
        })
        .catch((error) => {
          const errorData = error?.response?.data || {};
          const errorMessage =
            errorData?.non_field_errors?.[0] || errorData?.detail || this.$t("feedback.error.generic");

          toaster.error(this.$t("feedback.error.label"), {
            description: errorMessage,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template>
  <div class="flex flex-col space-y-4">
    <div class="basis-full">
      <div class="flex">
        <div class="basis-full">
          <SnsInput
            v-model="currentPassword"
            type="password"
            :label="$t('common.label.currentPassword')"
            :placeholder="$t('common.placeholder.enterCurrentPassword')"
            required
          />
        </div>
      </div>

      <div class="flex">
        <div class="basis-1/2 pr-2">
          <SnsInput
            v-model="newPassword"
            type="password"
            :label="$t('common.label.newPassword')"
            :placeholder="$t('common.label.newPassword')"
            required
          />
          <div v-if="passwordErrorMessage" class="label pt-0 error-message">
            {{ passwordErrorMessage }}
          </div>
        </div>

        <div class="basis-1/2 pl-2">
          <SnsInput
            v-model="newPasswordConfirm"
            type="password"
            :label="$t('common.label.confirmPassword')"
            :placeholder="$t('common.label.confirmPassword')"
            required
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.error-message {
  @apply text-error;
  font-size: 0.75rem;
  line-height: 1rem;
  animation: shake-vertical 100ms ease-in-out 3;
}
</style>
